import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { PrimeIcons } from 'primeng/api';
import {
  getListSettingsFieldConfig,
  ListSettingsFieldConfig,
} from 'src/app/models/list-settings-field-config';

@Component({
  selector: 'app-listsettings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ColorPickerModule,
    ButtonModule,
  ],
  templateUrl: './listsettings-dialog.component.html',
  styleUrl: './listsettings-dialog.component.scss',
})
export class ListsettingsDialogComponent implements OnInit {
  // Services
  private readonly fb = inject(FormBuilder);
  public readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  // Form
  public form!: FormGroup;

  // Icon-Konfiguration & Pagination
  private readonly PAGE_SIZE = 5;
  private readonly allIcons: string[] = Object.values(PrimeIcons);

  public currentItems: string[] = [];
  public currentItemIndex = 0;

  // Bestimmt, ob Icon-/Farbauswahl für die aktuell bearbeitete Liste angezeigt werden.
  public fieldConfig: ListSettingsFieldConfig =
    getListSettingsFieldConfig(undefined);

  public get showIcon(): boolean {
    return this.fieldConfig.hasIcon;
  }

  public get showColor(): boolean {
    return this.fieldConfig.hasColor;
  }

  ngOnInit(): void {
    this.fieldConfig = getListSettingsFieldConfig(
      this.config.data?.listSettingsName,
    );
    this.initForm();
    this.updateCurrentItems();
    this.setupColorSync();
  }

  /**
   * Initialisiert das reaktive Formular mit Validatoren.
   * Icon/Farbe sind nur Pflichtfelder, wenn die Liste sie unterstützt.
   */
  private initForm(): void {
    const { item } = this.config.data || {};

    this.form = this.fb.group({
      label: [item?.label || '', Validators.required],
      value: [item?.value || '', Validators.required],
      icon: [
        item?.icon || 'pi pi-file',
        this.showIcon ? Validators.required : [],
      ],
      color: [
        item?.color || '#3B82F6',
        this.showColor
          ? [
              Validators.required,
              // Regex für valide Hex-Codes (3 oder 6 Zeichen nach der Raute)
              Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/),
            ]
          : [],
      ],
    });
  }

  /**
   * Überwacht Farbänderungen und synchronisiert das Präfix sowie die Formatierung.
   * Verhindert durch { emitEvent: false } eine Endlosschleife.
   */
  private setupColorSync(): void {
    if (!this.showColor) return;

    const colorControl = this.form.get('color');
    if (!colorControl) return;

    colorControl.valueChanges.subscribe((val: string) => {
      if (!val) return;

      let normalized = val.toUpperCase();

      // Präfix sicherstellen
      if (!normalized.startsWith('#')) {
        normalized = `#${normalized}`;
      }

      // Länge auf max 7 Zeichen begrenzen (#RRGGBB)
      if (normalized.length > 7) {
        normalized = normalized.substring(0, 7);
      }

      // Nur patchen, wenn der Wert sich wirklich unterscheidet
      if (normalized !== val) {
        colorControl.patchValue(normalized, { emitEvent: false });
      }
    });
  }

  /**
   * Setzt das gewählte Icon im Formular.
   */
  public selectItem(iconName: string): void {
    this.form.patchValue({ icon: `pi ${iconName}` });
  }

  /**
   * Prüft für das UI-Styling, ob ein Icon aktiv ist.
   */
  public isSelected(iconName: string): boolean {
    return this.form.get('icon')?.value === `pi ${iconName}`;
  }

  /* --- Pagination Logik --- */

  public nextItem(): void {
    this.currentItemIndex =
      (this.currentItemIndex + this.PAGE_SIZE) % this.allIcons.length;
    this.updateCurrentItems();
  }

  public previousItem(): void {
    this.currentItemIndex =
      (this.currentItemIndex - this.PAGE_SIZE + this.allIcons.length) %
      this.allIcons.length;
    this.updateCurrentItems();
  }

  private updateCurrentItems(): void {
    this.currentItems = this.allIcons.slice(
      this.currentItemIndex,
      this.currentItemIndex + this.PAGE_SIZE,
    );
  }

  /* --- CRUD Actions --- */

  public save(): void {
    if (this.form.valid) {
      // Rückgabe des Objekts inkl. ID für die Weiterverarbeitung im Service.
      // Icon/Farbe werden nur mitgesendet, wenn die Liste sie unterstützt.
      const result: any = {
        label: this.form.value.label,
        value: this.form.value.value,
        id: this.config.data?.item?.id,
        listSettingsId:
          this.config.data?.listSettingsId ||
          this.config.data?.item?.listSettingsId,
      };

      if (this.showIcon) result.icon = this.form.value.icon;
      if (this.showColor) result.color = this.form.value.color;

      this.ref.close(result);
    }
  }
}
